import { useEffect, useState } from 'react';
import { KeyRound } from 'lucide-react';
import { matrixToPermissionStrings } from '@evi/shared';
import { fetchRfidPricingSettings, updateLocation } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { Switch } from '@/components/ui/Switch';
import styles from './ResidentChargersCard.module.css';

/** The string Location:update emits - what PUT /locations/:id checks
 *  (request.user.permissions.includes('location:write')). Derived from the
 *  shared resource map so it cannot drift from the server's gate. */
const LOCATION_WRITE_PERMISSION: string = matrixToPermissionStrings([{
  resource: 'Location', create: false, read: false, update: true, delete: false,
}] as never)[0] ?? 'location:write';

/**
 * Per-building fob self-registration toggle (Warren, 2026-09-25, F3:
 * "Residents self-register their own fobs ... make it a per-building setting
 * the Site admin can turn on").
 *
 *   - target field: Location.allow_resident_fob_registration (opt-IN,
 *     default off; the API gate reads `=== true`)
 *   - target endpoint: PUT /locations/:id, one of the three fields a Site
 *     persona may write (SITE_WRITABLE_LOCATION_FIELDS)
 *
 * Gated on the permission string the API checks, not on the persona: a
 * caller without location:write sees the state read-only instead of a switch
 * that 403s (charger_owner holds Station read-only until the reseed lands).
 *
 * The fee shown is the tenant's self_service price, which EVI sets; the
 * building decides whether, not how much. Same optimistic flip + inline
 * alert as LocationResidentManagedCard, and the same CSS module.
 */
interface LocationFobRegistrationCardProps {
  locationId: string;
  /** The building's tenant. The write mirror and the fee both depend on it:
   *  PUT /locations/:id only updates within the caller's tenant (a
   *  super_admin excepted), and the fee a resident pays is the BUILDING
   *  tenant's, which GET /settings/rfid-pricing can only show for the
   *  caller's own tenant. */
  locationTenantId: string;
  /** Current value, fed by the parent's Location state. */
  value: boolean;
  onChange?: (next: boolean) => void;
}

function formatFee(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function LocationFobRegistrationCard({
  locationId, locationTenantId, value, onChange,
}: LocationFobRegistrationCardProps) {
  const user = useAuthStore((s) => s.user);
  const isSuper = user?.role === 'super_admin';
  const sameTenant = user?.tenant_id === locationTenantId;
  // Mirrors PUT /locations/:id, which runs, in order: location:write, then
  // requireScope, then an update bounded to the caller's scope.
  //   - super_admin: bypasses all three.
  //   - tenant_admin without a template: scope bypass, own tenant only.
  //   - a Site persona (any template): holds location:write via Station, and
  //     only ever reaches the buildings in its AccessAssignment - the Site
  //     pages list exactly those.
  //   - operator: holds location:write but NOT the scope bypass, so without
  //     an assignment requireScope refuses it. Read-only here rather than a
  //     switch that 403s (the UI cannot see an operator's assignments).
  const hasWrite = (user?.permissions ?? []).includes(LOCATION_WRITE_PERMISSION);
  const canWrite = isSuper || (sameTenant && hasWrite && (
    (user?.role === 'tenant_admin' && !user?.template) || !!user?.template
  ));

  const [localValue, setLocalValue] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [feeCents, setFeeCents] = useState<number | null>(null);

  if (localValue !== value && !saving) {
    setLocalValue(value);
  }

  // The disclosed fee. A nice-to-have, never a render gate. Only fetched when
  // the building is in the caller's own tenant: the pricing GET answers for
  // the caller's tenant, and showing it for another tenant's building would
  // name a fee that building's residents do not pay.
  useEffect(() => {
    if (!sameTenant) return;
    let cancelled = false;
    fetchRfidPricingSettings()
      .then((r) => {
        const ss = r.value['self_service'];
        if (!cancelled && ss) setFeeCents(ss.payer === 'driver' ? ss.amount_cents : 0);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [sameTenant]);

  const handleToggle = async (next: boolean): Promise<void> => {
    if (saving || !canWrite) return;
    const previous = localValue;
    setLocalValue(next);
    setSaving(true);
    setError('');
    try {
      await updateLocation(locationId, { allow_resident_fob_registration: next });
      setSavedAt(Date.now());
      onChange?.(next);
    } catch (err) {
      setLocalValue(previous);
      setError(err instanceof Error ? err.message : 'Failed to save');
      // eslint-disable-next-line no-console
      console.error('LocationFobRegistrationCard update failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // A fleet admin reads the site-safe Location shape, which strips this
  // field, so any state shown here would be invented.
  if (user?.role === 'fleet_admin') return null;

  const feeLine = feeCents === null
    ? null
    : feeCents > 0
      ? `Each driver pays a one-time ${formatFee(feeCents)} from their own saved card, shown to them before they confirm.`
      : 'Registration is free for drivers.';

  return (
    <section className={styles.card}>
      <div className={styles.cardTitleRow}>
        <h2 className={styles.cardTitle}>
          <KeyRound size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          Fob Self-Registration
        </h2>
      </div>

      {error && <div className={styles.alertErr}>{error}</div>}
      {savedAt && !error && <div className={styles.alertOk}>Saved.</div>}

      <div className={styles.residentToggleRow}>
        <div className={styles.residentToggleInfo}>
          <p className={styles.residentToggleLabel}>
            Let drivers register their own fobs here
          </p>
          <p className={styles.residentToggleHint}>
            When on, a driver taps an unregistered fob at one of this
            building&apos;s chargers from the EVI app and it becomes theirs.
            {feeLine ? ` ${feeLine}` : ''} Any EVI driver can do this at a
            building that is on; it is not limited to this building&apos;s
            residents. Building staff never issue fobs. When off, nobody can
            register a fob here.
          </p>
          {!canWrite && (
            <p className={styles.residentToggleHint}>
              {localValue ? 'On' : 'Off'} for this building. Your account can view this setting but not change it.
            </p>
          )}
        </div>
        {canWrite && (
          <Switch
            hideLabel
            label="Let drivers register their own fobs here"
            checked={localValue}
            disabled={saving}
            onChange={(e) => void handleToggle(e.target.checked)}
          />
        )}
      </div>
    </section>
  );
}
